import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Cookie from "js-cookie";
import URLs from "constants/urls";
import {
  StyledButton,
  StyledCancelButton,
  ModernHeader,
  inputStyle,
  StyledAgGridContainer,
} from "components/StyledComponents/styledComponents.js";

const formatCurrencyINR = (value) =>
  `₹ ${(Number(value) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (isNaN(date)) return value;
  const day = date.toLocaleString("en-IN", { day: "2-digit" });
  const month = date.toLocaleString("en-IN", { month: "short" });
  return `${day}-${month}-${date.getFullYear()}`;
};

const gstIncludedText = (value) => (value === "Y" ? "Received" : value === "N" ? "Pending" : "-");

const ConnectorPendingGst = ({ setAlert, setLoading, dsaId = 0 }) => {
  const [rowData, setRowData] = useState([]);
  const [search, setSearch] = useState("");
  const MInput = inputStyle();
  const [selectedRow, setSelectedRow] = useState(null);
  const [tranDate, setTranDate] = useState(new Date().toISOString().split("T")[0]);

  const fetchPendingGst = async () => {
    const token = Cookie.get("jwt_token");
    if (!token) {
      setAlert("Session expired. Please log in again.", "error");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${URLs.connectorPaymentsPendingGst}/${dsaId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (response.status === 204) {
        setRowData([]);
        return;
      }
      if (!response.ok) throw new Error(`Failed to fetch pending GST (${response.status})`);
      const data = await response.json();
      setRowData(Array.isArray(data) ? data : []);
    } catch (error) {
      setRowData([]);
      setAlert(`Failed to fetch connector pending GST: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const closeConfirmDialog = () => setSelectedRow(null);

  const handleProcessGst = async () => {
    if (!tranDate) {
      setAlert("Please select a date", "error");
      return;
    }
    const token = Cookie.get("jwt_token");
    if (!token) {
      setAlert("Session expired. Please log in again.", "error");
      return;
    }
    const row = selectedRow;
    closeConfirmDialog();
    setLoading(true);
    try {
      const response = await fetch(URLs.connectorPaymentsProcessGst, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          entryId: row.entryId,
          paymentId: row.paymentId,
          dsaId: row.dsaId ?? dsaId,
          subDsaId: row.subDsaId,
          isGstIncluded: "Y",
          tranDate,
        }),
      });
      if (!response.ok) throw new Error(`Failed to process GST (${response.status})`);
      const message = await response.text();
      setAlert(message || "GST processed successfully", "success");
    } catch (error) {
      setAlert(`Failed to process GST: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
    fetchPendingGst();
  };

  useEffect(() => {
    fetchPendingGst();
  }, [dsaId]);

  const filteredData = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rowData;
    return rowData.filter((row) =>
      [row.subDsaName, row.subDsaState, row.createdBy, row.entryId, row.voucherId].some((field) =>
        String(field ?? "")
          .toLowerCase()
          .includes(query)
      )
    );
  }, [rowData, search]);

  const footerData = useMemo(() => {
    const sum = (key) => filteredData.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);
    return [
      {
        subDsaName: `Total (${filteredData.length})`,
        subDsaPaymentAmount: sum("subDsaPaymentAmount"),
        tds: sum("tds"),
        finalSubDsaGrossPayoutAmount: sum("finalSubDsaGrossPayoutAmount"),
        gstAmount: sum("gstAmount"),
        totalAmount: sum("totalAmount"),
      },
    ];
  }, [filteredData]);

  const currencyCol = {
    type: "rightAligned",
    filter: "agNumberColumnFilter",
    valueFormatter: (params) => formatCurrencyINR(params.value),
  };

  const columnDefs = [
    {
      headerName: "Tran Date",
      field: "tranDate",
      valueFormatter: (params) => (params.node.rowPinned ? "" : formatDate(params.value)),
      minWidth: 120,
    },
    { headerName: "Entry ID", field: "entryId", minWidth: 100 },
    { headerName: "Voucher ID", field: "voucherId", minWidth: 110 },
    { headerName: "Connector", field: "subDsaName", minWidth: 200 },
    { headerName: "State", field: "subDsaState", minWidth: 150 },
    {
      headerName: "Payout %",
      field: "subDsaPayoutPercent",
      type: "rightAligned",
      valueFormatter: (params) =>
        params.node.rowPinned ? "" : `${(Number(params.value) || 0).toFixed(2)}%`,
      minWidth: 100,
    },
    { headerName: "Payment Amount", field: "subDsaPaymentAmount", minWidth: 150, ...currencyCol },
    { headerName: "GST Amount", field: "gstAmount", minWidth: 140, ...currencyCol },
    { headerName: "Total Amount", field: "totalAmount", minWidth: 150, ...currencyCol },
    { headerName: "TDS", field: "tds", minWidth: 120, ...currencyCol },
    {
      headerName: "Net Paid",
      field: "finalSubDsaGrossPayoutAmount",
      minWidth: 150,
      ...currencyCol,
    },

    {
      headerName: "GST Included",
      field: "isGstIncluded",
      valueGetter: (params) =>
        params.node.rowPinned ? "" : gstIncludedText(params.data?.isGstIncluded),
      minWidth: 120,
    },
    {
      headerName: "Action",
      field: "action",
      cellRenderer: (params) => {
        if (params.node.rowPinned || params.data?.isGstIncluded === "Y") return null;
        return (
          <Tooltip title="Mark as GST Processed">
            <IconButton onClick={() => setSelectedRow(params.data)} color="success" size="small">
              <CheckCircleIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
        );
      },
      pinned: "right",
      width: 90,
      minWidth: 90,
      maxWidth: 90,
      flex: 0,
      filter: false,
      sortable: false,
      resizable: false,
    },
  ];

  const defaultColDef = {
    sortable: true,
    filter: true,
    floatingFilter: true,
    resizable: true,
    flex: 1,
  };

  return (
    <>
      <ModernHeader label="Connector Payments – Pending GST" />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 2, mt: 2, alignItems: "center" }}
      >
        <TextField
          label="Search Connector, State, Entry..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ ...MInput.b, minWidth: "260px" }}
          InputLabelProps={{
            shrink: true,
            sx: { color: MInput.a },
          }}
        />
        <StyledButton onClick={fetchPendingGst}>Refresh</StyledButton>
      </Stack>
      <StyledAgGridContainer
        component={Paper}
        className="ag-theme-alpine"
        sx={{ minHeight: "500px", mb: 2 }}
      >
        <AgGridReact
          enableCellTextSelection
          columnDefs={columnDefs}
          rowData={filteredData}
          defaultColDef={defaultColDef}
          pagination
          paginationPageSize={100}
          pinnedBottomRowData={footerData}
          getRowStyle={(params) =>
            params.node.rowPinned ? { fontWeight: "bold", backgroundColor: "#f1f5f9" } : undefined
          }
          overlayNoRowsTemplate="No pending GST connector payments"
          animateRows
        />
      </StyledAgGridContainer>
      <Dialog open={Boolean(selectedRow)} onClose={closeConfirmDialog} fullWidth maxWidth="xs">
        <DialogTitle>Confirm GST Processing</DialogTitle>
        <DialogContent>
          Mark GST as processed for {selectedRow?.subDsaName} (Entry {selectedRow?.entryId})?
          <TextField
            label="Select Date"
            type="date"
            value={tranDate}
            onChange={(e) => setTranDate(e.target.value)}
            fullWidth
            sx={{ ...MInput.b, mt: 2 }}
            InputLabelProps={{
              shrink: true,
              sx: { color: MInput.a },
            }}
          />
        </DialogContent>
        <DialogActions>
          <StyledCancelButton onClick={closeConfirmDialog} color="inherit">
            Cancel
          </StyledCancelButton>
          <StyledButton onClick={handleProcessGst} variant="contained" color="success">
            Confirm
          </StyledButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

ConnectorPendingGst.propTypes = {
  setAlert: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  dsaId: PropTypes.number,
};

export default ConnectorPendingGst;
